import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';

const Gallery = () => {
    const [images, setImages] = useState([]);
    const [lightbox, setLightbox] = useState(null);
    const ref = useRef(null);

    useEffect(() => {
        const fetchAll = async (url, acc = []) => {
            try {
                if (url.startsWith('http:')) url = url.replace('http:', 'https:');
                const res = await axios.get(url);
                const all = [...acc, ...res.data.results];
                if (res.data.next) return fetchAll(res.data.next, all);
                setImages(all);
            } catch (e) {
                console.error(e);
            }
        };
        fetchAll('https://kyrgyz-adventure-travel.com/api/v1/tours/about-us-images-list');
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
            { threshold: 0.1 }
        );
        ref.current?.querySelectorAll('.reveal').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    // Close lightbox on Escape
    useEffect(() => {
        if (lightbox === null) return;
        const onKey = (e) => {
            if (e.key === 'Escape') setLightbox(null);
            if (e.key === 'ArrowRight') setLightbox(i => (i + 1) % images.length);
            if (e.key === 'ArrowLeft') setLightbox(i => (i - 1 + images.length) % images.length);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [lightbox, images.length]);

    // Block body scroll when lightbox open
    useEffect(() => {
        document.body.style.overflow = lightbox !== null ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [lightbox]);

    if (images.length < 4) return null;

    // Split into 2 rows
    const mid = Math.ceil(images.length / 2);
    const row1 = images.slice(0, mid);
    const row2 = images.slice(mid);

    // All images flat for lightbox navigation
    const allImages = images;

    return (
        <div ref={ref} className="bg-dark py-24 md:py-36 overflow-hidden">
            {/* Header */}
            <div className="container mb-14">
                <div className="reveal flex flex-col items-center gap-4 text-center">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-px bg-it-green opacity-90" />
                        <p className="font-unbounded text-white text-xs uppercase tracking-widest">Our world</p>
                        <div className="w-8 h-px bg-gold opacity-70" />
                    </div>
                    <h2 className="font-unbounded font-bold text-4xl md:text-5xl text-white">
                        Kyrgyzstan Travel <span className="text-gold">Gallery</span>
                    </h2>
                    <p className="font-manrope text-white/50 text-lg max-w-xl leading-relaxed mt-2">
                        Real moments from real adventures. Click any photo to explore.
                    </p>
                </div>
            </div>

            {/* Row 1 — scrolls left */}
            <MarqueeRow images={row1} direction="left" onClickImg={(img) => setLightbox(allImages.findIndex(i => i.image === img.image))} />

            <div className="h-4" />

            {/* Row 2 — scrolls right */}
            <MarqueeRow images={row2} direction="right" onClickImg={(img) => setLightbox(allImages.findIndex(i => i.image === img.image))} />

            {/* Lightbox */}
            {lightbox !== null && (
                <div
                    className="lightbox-backdrop fixed inset-0 z-[100] flex items-center justify-center"
                    style={{ background: 'rgba(0,0,0,0.92)' }}
                    onClick={() => setLightbox(null)}
                >
                    {/* Close */}
                    <button
                        onClick={() => setLightbox(null)}
                        className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors duration-200 z-10"
                    >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path d="M1 1l16 16M17 1L1 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>

                    {/* Counter */}
                    <div className="absolute top-6 left-1/2 -translate-x-1/2 font-unbounded text-white/50 text-xs tracking-widest">
                        {lightbox + 1} / {allImages.length}
                    </div>

                    {/* Prev */}
                    <button
                        onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + allImages.length) % allImages.length); }}
                        className="absolute left-4 md:left-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white transition-all duration-200 hover:scale-110"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M13 4l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>

                    {/* Image */}
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="relative max-w-5xl w-full mx-16 md:mx-24"
                    >
                        <img
                            key={lightbox}
                            src={allImages[lightbox]?.image}
                            alt=""
                            className="w-full max-h-[80vh] object-contain rounded-2xl lightbox-img"
                        />
                    </div>

                    {/* Next */}
                    <button
                        onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % allImages.length); }}
                        className="absolute right-4 md:right-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white transition-all duration-200 hover:scale-110"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M7 4l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
};

const MarqueeRow = ({ images, direction, onClickImg }) => {
    const [paused, setPaused] = useState(false);
    // Duplicate for seamless loop
    const doubled = [...images, ...images];

    return (
        <div
            className="relative w-full"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            {/* Fade edges */}
            <div className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
                 style={{ background: 'linear-gradient(to right, #0d0d0d, transparent)' }} />
            <div className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
                 style={{ background: 'linear-gradient(to left, #0d0d0d, transparent)' }} />

            <div
                className={`flex gap-4 ${direction === 'left' ? 'marquee-left' : 'marquee-right'}`}
                style={{ animationPlayState: paused ? 'paused' : 'running', width: 'max-content' }}
            >
                {doubled.map((img, i) => (
                    <div
                        key={i}
                        onClick={() => onClickImg(img)}
                        className="relative flex-shrink-0 w-72 h-52 rounded-2xl overflow-hidden cursor-pointer group hover:scale-105 active:scale-95 transition-transform duration-300 hover:z-10"
                    >
                        <img
                            src={img.image}
                            alt=""
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            loading="lazy"
                        />
                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-dark/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                            <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                    <path d="M1 1h6M1 1v6M17 17h-6M17 17v-6M1 17h6M1 17v-6M17 1h-6M17 1v6" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                            </div>
                        </div>
                        {/* Bottom shimmer */}
                        <div className="absolute bottom-0 left-0 right-0 h-16 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                             style={{ background: 'linear-gradient(to top, rgba(0,146,70,0.3), transparent)' }} />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Gallery;
